import { Injectable } from '@angular/core';
import gql from 'graphql-tag';
import { filter, first, switchMap, timeout } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { UiPromiseService } from 'ngx-zcs-components';

import { SendCommandForAzzurroHubMutation, OnReceiveAckSubscription, ChangeAhHeatPumpConfigMutation, VRTManagementAzzurroHubMutation } from '../telemetry/index.graph-ql';
import { AzzurroHubAction, AzzurroHubBody, AzzurroHubOperation, PlantPhasesInput, MonitoringModeInput, IntSensorInversionInput, ChangePlantConfigInput, ChangeAhHeatPumpConfigBody, ReceiveAckResponse, VirtualRTAzzurroHubTarget } from '../telemetry';
import { GraphqlApiService } from '../graphql-api/graphql-api.service';
import { isNotNullOrUndefined, generateTransactionId } from '../utils';
import { AzzurroHubErrorCode, CmdAzzurroHubError } from './cmd-azzurro.error';

const commandOpts = {
  timeout: 80000,
  errorMessage: 'Timeout exceeded',
  loading: true,
  loadingMessage: 'Sending Azzurro Hub command',
};

const receiveAckResponseQuery = gql`
  fragment receiveackresponse on ReceiveAckResponse {
    transactionId
    version
    deviceId
    timestamp
    dataType
    message
    success
  }`;

const azzurroHubResponseQuery = gql`
  fragment cmdreq on CMDReq {
    action {
      msg
      msgCode
      success
    }
    clientId
    deviceId
    timestamp
    transactionId
  }`;

const vrtManagementResponseQuery = gql`
  fragment startvirtualrtazzurrohubresponse on startVirtualRTAzzurroHubResponse {
    success
    msg
    code
  }`;

@Injectable({
  providedIn: 'root',
})
export class CmdAzzurroHubService {
  readonly client$ = this.graphqlApiService.client$.pipe(
    isNotNullOrUndefined(),
  );

  constructor(
    private readonly changeAhHeatPumpConfigMutation : ChangeAhHeatPumpConfigMutation,
    private readonly graphqlApiService: GraphqlApiService,
    private readonly onReceiveAckSubscription: OnReceiveAckSubscription,
    private readonly sendCommandForAzzurroHubMutation: SendCommandForAzzurroHubMutation,
    private readonly vrtManagementAzzurroHubMutation: VRTManagementAzzurroHubMutation,
    private readonly uiPromiseService: UiPromiseService,
  ) {}

  private async waitForAck(
  deviceId: string,
  transactionId: string,
  opts = commandOpts
) {
  // ! pay attention to the correct functioning of this ack receiving methodology
  const response$ = new Observable<Partial<ReceiveAckResponse>>((observer) => {
    const outer$ = this.onReceiveAckSubscription.execute(
      receiveAckResponseQuery,
      { deviceId },
    );
    const outerSub = outer$.pipe(first()).subscribe({
      next: (inner$: any) => {
        const innerSub = inner$.subscribe({
          next: (res: { data: { onReceiveAck: Partial<ReceiveAckResponse> } }) => {
            observer.next(res.data.onReceiveAck);
          },
          error: (err: any) => observer.error(err),
          complete: () => observer.complete(),
        });
        (observer as any)._innerSub = innerSub;
      },
      error: (err: any) => observer.error(err),
      complete: () => {},
    });

    return () => {
      outerSub.unsubscribe();
      const innerSub = (observer as any)._innerSub;
      if (innerSub && typeof innerSub.unsubscribe === 'function') {
        innerSub.unsubscribe();
      }
    };
  }).pipe(
    filter((ackResponse) =>
      ackResponse?.transactionId === transactionId &&
      ackResponse?.success !== null &&
      ackResponse?.success !== undefined
    ),
    first(),
    timeout(opts.timeout),
    switchMap((ackResponse) => {
      // WHEN success is false azzurro hub returned an error
      if (ackResponse.success === false) {
        return throwError(() => new CmdAzzurroHubError(
          ackResponse.message || 'Azzurro Hub command failed',
          AzzurroHubErrorCode.UnknownError
        ));
      }
      return of(ackResponse);
    }),
  );

  const ackResponse = await response$.toPromise().catch((err) => {
    console.log('ERROR in waitForAck', err);
    if (err.name === 'TimeoutError') {
      throw new CmdAzzurroHubError(opts.errorMessage, AzzurroHubErrorCode.Timeout);
    }
    throw err;
  });

  return ackResponse;
}

  /**
   * Core method to send commands to Azzurro Hub with subscription to response
   */
  private async sendCommand(
    deviceId: string,
    action: AzzurroHubAction,
    body: AzzurroHubBody,
    opts = commandOpts
  ) {
    const transactionId = generateTransactionId();

    const cmd$ = of(null).pipe(
      switchMap(() => this.sendCommandForAzzurroHubMutation.execute(azzurroHubResponseQuery, {
        deviceId,
        action,
        body,
        transactionId
      })),
    );

    return await this.uiPromiseService.run(
      async () => {
        // Start listening for ACK BEFORE sending the command to avoid race conditions
        // where the device ACKs before the subscription is established
        const ackPromise = this.waitForAck(deviceId, transactionId, opts);

        let commandResult;
        try {
          commandResult = await cmd$.toPromise();

          if (!commandResult.data?.sendCommandForAzzurroHub.action?.success) {
            const action = commandResult.data?.sendCommandForAzzurroHub.action;
            const errorCode = action?.msgCode ? action.msgCode.toString() as AzzurroHubErrorCode : AzzurroHubErrorCode.UnknownError;
            throw new CmdAzzurroHubError(
              action?.msg || 'Comando Azzurro Hub fallito',
              errorCode
            );
          }
        } catch (e) {
          ackPromise.catch(() => {}); // Prevent unhandled rejection from timed-out subscription
          throw e;
        }

        const ackResponse = await ackPromise;

        return {
          command: commandResult.data.sendCommandForAzzurroHub,
          response: ackResponse
        };
      },
      {
        loading: opts.loading,
        loadingMessage: opts.loadingMessage,
      },
    );
  }

  /**
   * 1. CONFIGURAZIONE LOGGER
   * Equivalente al comando "reg i UA" di Connext
   * Configura timezone e paese
   */
  async configureLogger(
    deviceId: string,
    timezone: string,
    country: string,
    loadingMessage: string
  ) {
    return await this.sendCommand(deviceId, AzzurroHubAction.changeLoggerConfig, {
      changeLoggerConfig: {
        IANA: timezone,
        country: country
      }
    }, { ...commandOpts, loadingMessage });
  }

  /**
   * 2. CONFIGURAZIONE IMPIANTO
   * Equivalente al comando "set plant" di Connext
   * Configura potenze, fasi e modalità monitoraggio
   */
  async configurePlant(
    deviceId: string,
    contractualPower: number,
    plantType: PlantPhasesInput,
    monitoringMode: MonitoringModeInput,
    sensorInversion: IntSensorInversionInput,
    loadingMessage: string
  ) {
    return await this.sendCommand(deviceId, AzzurroHubAction.changePlantConfig, {
      changePlantConfig: {
        maxContractualPwr: contractualPower,
        plantPhases: plantType,
        monitoringMode: monitoringMode,
        intSensorInversion: sensorInversion
      }
    }, { ...commandOpts, loadingMessage });
  }

  /**
   * 3. GESTIONE DISPOSITIVI - RESCAN
   * Scansione automatica dispositivi collegati
   */
  async rescanDevices(
    deviceId: string,
    loadingMessage: string
  ) {
    return await this.sendCommand(deviceId, AzzurroHubAction.changeDeviceStatus, {
      changeDeviceStatus: {
        operation: AzzurroHubOperation.rescan,
      }
    }, { ...commandOpts, loadingMessage });
  }

  /**
   * 4. CONFIGURAZIONE EV CHARGE
   * Configura i parametri del caricatore elettrico
   */
  async configureEvcharge(
    deviceId: string,
    changePlantConfig: ChangePlantConfigInput,
    loadingMessage: string
  ) {
    return await this.sendCommand(
      deviceId,
      AzzurroHubAction.changePlantConfig,
      { changePlantConfig },
      { ...commandOpts, loadingMessage }
    );
  }

  async configureAzzurroHubHeatPump(
    deviceId: string,
    body: ChangeAhHeatPumpConfigBody,
    loadingMessage: string
  ) {

    const transactionId = generateTransactionId();

    // Send the command
    const cmd$ = of(null).pipe(
      switchMap(() => this.changeAhHeatPumpConfigMutation.execute(azzurroHubResponseQuery, {
        loggerId: deviceId,
        body,
        transactionId
      })),
    );

    return await this.uiPromiseService.run(
      async () => {
        // First send the command and check if it was accepted
        const commandResult = await cmd$.toPromise();

        if (!commandResult.data?.changeAhHeatPumpConfig?.action?.success) {
          const action = commandResult.data?.changeAhHeatPumpConfig.action;
          const errorCode = action?.msgCode ? action.msgCode.toString() as AzzurroHubErrorCode : AzzurroHubErrorCode.UnknownError;
          throw new CmdAzzurroHubError(
            action?.msg || 'Comando Azzurro Hub fallito',
            errorCode
          );
        }

        // Then wait for the actual response via subscription
        const ackResponse = await this.waitForAck(deviceId, transactionId, { ...commandOpts, loadingMessage });

        return {
          command: commandResult.data.changeAhHeatPumpConfig,
          response: ackResponse
        };
      },
      {
        loading: commandOpts.loading,
        loadingMessage: commandOpts.loadingMessage,
      },
    );
  }

  /**
   * Virtual RT management through the standard command flow:
   * send mutation with transactionId and wait for ACK on onReceiveAck.
   */
  async manageVirtualRT(
    loggerId: string,
    active: boolean,
    duration?: number,
    target: VirtualRTAzzurroHubTarget = VirtualRTAzzurroHubTarget.ZZ,
    loadingMessage = active ? 'Starting Real-Time telemetry' : 'Stopping Real-Time telemetry',
  ) {
    const transactionId = generateTransactionId();
    const opts = { ...commandOpts, loadingMessage };

    const mutation$ = of(null).pipe(
      switchMap(() => this.vrtManagementAzzurroHubMutation.execute(vrtManagementResponseQuery, {
        transactionId,
        loggerId,
        active,
        target,
        ...(active && duration !== undefined ? { duration } : {}),
      })),
    );

    return await this.uiPromiseService.run(
      async () => {
        const mutationResult = await mutation$.toPromise();
        const action = mutationResult.data?.VRTManagementAzzurroHub;

        if (!action?.success) {
          const errorCode = action?.code ? `${action.code}` as AzzurroHubErrorCode : AzzurroHubErrorCode.UnknownError;
          throw new CmdAzzurroHubError(
            action?.msg || 'Virtual RT command failed',
            errorCode,
          );
        }

        const ackResponse = await this.waitForAck(loggerId, transactionId, opts);

        return {
          command: action,
          response: ackResponse,
          transactionId,
        };
      },
      {
        loading: commandOpts.loading,
        loadingMessage: commandOpts.loadingMessage,
      },
    );
  }

  /**
   * Strategy method per configurazione completa Azzurro Hub
   * Simile a sendCommandStrategy di CmdConnextService
   */
  async configureAzzurroHubStrategy(
    deviceId: string,
    config: {
      timezone: string;
      country: string;
      contractualPower: number;
      plantType: PlantPhasesInput;
      monitoringMode?: MonitoringModeInput;
      sensorInversion?: IntSensorInversionInput;
    },
    loadingMessage: string
  ): Promise<{ error?: CmdAzzurroHubError; success?: any; }> {
    try {
      // 1. Configurazione Logger (timezone e paese)
      const loggerResponse = await this.configureLogger(
        deviceId,
        config.timezone,
        config.country,
        loadingMessage
      );

      if (loggerResponse.error) {
        return { error: loggerResponse.error };
      }

      if (!loggerResponse.success?.response?.success) {
        const msg = loggerResponse.success?.response?.message || 'Logger configuration failed';
        return {
          error: new CmdAzzurroHubError(msg, AzzurroHubErrorCode.UnknownError),
        };
      }

      // 2. Configurazione Impianto (potenze e parametri)
      const plantResponse = await this.configurePlant(
        deviceId,
        config.contractualPower,
        config.plantType,
        config.monitoringMode || MonitoringModeInput.invMaster,
        config.sensorInversion || IntSensorInversionInput.none,
        loadingMessage
      );

      if (plantResponse.error) {
        return { error: plantResponse.error };
      }

      if (!plantResponse.success?.response?.success) {
        const msg = plantResponse.success?.response?.message || 'Plant configuration failed';
        return {
          error: new CmdAzzurroHubError(msg, AzzurroHubErrorCode.UnknownError),
        };
      }

      return { success: true };
    } catch (error: any) {
      return { error: new CmdAzzurroHubError(error.message || 'Configuration failed', AzzurroHubErrorCode.UnknownError) };
    }
  }
}
